import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PlayIcon,
  CalendarDaysIcon,
  CloudIcon,
  UserGroupIcon,
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  formatDateTime,
  formatTradingDay,
  statusBadge,
  buildGamingImportResultMessage,
  ImportResultBanner,
} from './dailyDataUtils'
import DailyDataNav from './DailyDataNav'
import ImportConfirmModal from './ImportConfirmModal'

export default function VisitorFeed() {
  const apiBase = '/api/daily-data/visitor'

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testPreview, setTestPreview] = useState(null)
  const [message, setMessage] = useState(null)
  const [tradingDay, setTradingDay] = useState('')
  const [confirmOpts, setConfirmOpts] = useState(null)
  const resultRef = useRef(null)

  const showResult = useCallback((result, extra = {}) => {
    setMessage({ ...result, ...extra })
    requestAnimationFrame(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(apiBase))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Failed to load visitor feed')
      setData(json.data)
      if (!tradingDay && json.data?.trading_day_today) {
        setTradingDay(json.data.trading_day_yesterday || json.data.trading_day_today)
      }
      return true
    } catch (e) {
      showResult({ type: 'error', summary: e.message, errorDetails: [] })
      return false
    } finally {
      setLoading(false)
    }
  }, [apiBase, showResult, tradingDay])

  // Header Refresh: re-read feed status with visible feedback. Never starts an import.
  const handleRefresh = async () => {
    if (loading) return
    const ok = await load()
    if (ok) toast.success('Refreshed')
    else toast.error('Refresh failed')
  }

  useEffect(() => {
    load()
  }, [load])

  const runTest = async () => {
    if (!tradingDay) return
    setTesting(true)
    setTestPreview(null)
    setMessage(null)
    try {
      const qs = new URLSearchParams({ trading_day: tradingDay })
      const res = await authFetch(getLocalApiUrl(`${apiBase}/preview?${qs}`))
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Test failed')
      setTestPreview(json.data)
    } catch (e) {
      setTestPreview({
        ready: false,
        connected: false,
        error: e.message || 'Test failed',
      })
    } finally {
      setTesting(false)
    }
  }

  const runImport = async (opts = {}) => {
    setRunning(true)
    setMessage(null)
    try {
      const body = {}
      if (opts.tradingDay) body.trading_day = opts.tradingDay
      const res = await authFetch(getLocalApiUrl(`${apiBase}/run`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Import failed')
      const d = json.data
      const result = buildGamingImportResultMessage(d, {
        feedLabel: 'Visitors',
        tradingDay: opts.tradingDay ?? tradingDay,
      })
      if (d.trading_day) setTradingDay(d.trading_day)
      showResult(result)
      setConfirmOpts(null)
      await load()
    } catch (e) {
      showResult({
        type: 'error',
        summary: `Visitors import failed — ${e.message}`,
        errorDetails: [],
      })
    } finally {
      setRunning(false)
    }
  }

  const source = data?.visitor_source
  const isApi = source === 'api'
  const sourceLabel = data?.visitor_source_label || (isApi ? 'RXConnect API' : 'File extract')

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <DailyDataNav />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/monitor-safe/daily-data" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2">
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Daily Data
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserGroupIcon className="w-7 h-7 text-primary-600" />
            Visitors
          </h1>
          <p className="text-gray-600 mt-1">
            {isApi
              ? 'Import visitor check-ins from RXConnect (RxGetVisitorEntries) for the selected trading day.'
              : 'Import DimVisitor extracts into visitor_entries.'}
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {data?.feed_status && statusBadge(data.feed_status.status)}
          <span className="text-sm text-gray-600">
            Source: <strong>{sourceLabel}</strong>
          </span>
          {!source && (
            <span className="text-sm text-amber-700">
              Enable visitors and configure VISITOR/RXCONNECT in third_party_interface_data, or set visitor_source to file.
            </span>
          )}
        </div>

        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-6">
          <div>
            <dt className="text-gray-500 text-xs uppercase">Trading day</dt>
            <dd className="font-medium">{formatTradingDay(data?.trading_day_today)}</dd>
          </div>
          <div>
            <dt className="text-gray-500 text-xs uppercase">Last run</dt>
            <dd className="font-medium">{formatDateTime(data?.latest_run?.run_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500 text-xs uppercase">Template</dt>
            <dd className="font-medium">{isApi ? 'RXConnect API' : (data?.default_template_name || 'Default')}</dd>
          </div>
          <div>
            <dt className="text-gray-500 text-xs uppercase">Enabled</dt>
            <dd className="font-medium">{data?.config?.visitor_enabled ? 'Yes' : 'No'}</dd>
          </div>
        </dl>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
              Import trading day
            </label>
            <input
              type="date"
              value={tradingDay}
              onChange={(e) => setTradingDay(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <button
            type="button"
            disabled={testing || !source || !tradingDay}
            onClick={runTest}
            className="inline-flex items-center px-4 py-2 border border-primary-600 text-primary-700 rounded-md text-sm font-medium hover:bg-primary-50 disabled:opacity-50"
          >
            <BeakerIcon className="w-4 h-4 mr-1" />
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button
            type="button"
            disabled={running || !data?.config?.visitor_enabled || !source}
            onClick={() => setConfirmOpts({ tradingDay })}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            title={!data?.config?.visitor_enabled ? 'Enable visitor import in Settings first' : undefined}
          >
            <PlayIcon className="w-4 h-4 mr-1" />
            {running ? 'Running…' : 'Run import'}
          </button>
        </div>

        {!data?.config?.visitor_enabled && source && (
          <p className="text-xs text-amber-700 mt-2">
            Visitor import is disabled — you can still <strong>Test connection</strong> to verify RXConnect returns data before enabling import in Settings.
          </p>
        )}

        {testPreview && (
          <div className={`mt-4 rounded-lg border p-4 text-sm ${
            testPreview.connected !== false && testPreview.ready !== false
              ? 'border-green-200 bg-green-50'
              : 'border-red-200 bg-red-50'
          }`}>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 font-medium text-gray-900">
                {testPreview.connected !== false && testPreview.ready !== false ? (
                  <>
                    <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                    Connection OK — sample data received
                  </>
                ) : (
                  <>
                    <XCircleIcon className="w-5 h-5 text-red-600 shrink-0" />
                    Test failed
                  </>
                )}
              </div>
              <button
                type="button"
                className="text-xs text-gray-500 hover:text-gray-800"
                onClick={() => setTestPreview(null)}
              >
                Dismiss
              </button>
            </div>

            {testPreview.error && (
              <p className="text-red-800 mb-2">{testPreview.error}</p>
            )}

            {testPreview.ready !== false && !testPreview.error && (
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                <div>
                  <dt className="text-gray-500 uppercase">Trading day</dt>
                  <dd className="font-medium">{formatTradingDay(testPreview.trading_day)}</dd>
                </div>
                {isApi && testPreview.window && (
                  <div className="md:col-span-2">
                    <dt className="text-gray-500 uppercase">API window</dt>
                    <dd className="font-mono text-xs">
                      {testPreview.window.fromDateTime} → {testPreview.window.toDateTime}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-gray-500 uppercase">Records (sample)</dt>
                  <dd className="font-medium">
                    {testPreview.total_count ?? testPreview.sample_visitors?.length ?? 0}
                    {testPreview.has_more ? '+' : ''}
                  </dd>
                </div>
                {!isApi && testPreview.file?.name && (
                  <div className="md:col-span-2">
                    <dt className="text-gray-500 uppercase">File</dt>
                    <dd className="font-mono truncate" title={testPreview.file.path}>{testPreview.file.name}</dd>
                  </div>
                )}
              </dl>
            )}

            {testPreview.sample_visitors?.length > 0 && (
              <div className="overflow-x-auto rounded border border-white/60 bg-white">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Entry ID</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Entry time</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Member #</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Type</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {testPreview.sample_visitors.map((v) => (
                      <tr key={v.entry_id}>
                        <td className="px-3 py-2 font-mono">{v.entry_id}</td>
                        <td className="px-3 py-2">{v.visitor_name || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(v.entry_datetime)}</td>
                        <td className="px-3 py-2 font-mono">{v.member_id || '—'}</td>
                        <td className="px-3 py-2">{v.visitor_entry_type || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {testPreview.ready !== false && !testPreview.error && !testPreview.sample_visitors?.length && (
              <p className="text-gray-700">
                Connected successfully but no visitor entries returned for this trading day.
                {isApi ? ' The API window may be correct — try an adjacent day with known foot traffic.' : ''}
              </p>
            )}

            {testPreview.has_more && (
              <p className="text-xs text-gray-600 mt-2">
                Showing first {testPreview.sample_visitors?.length || 0} of {testPreview.total_count}+ entries — run import to load all pages.
              </p>
            )}
          </div>
        )}

        {!isApi && source === 'file' && (
          <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
            <CloudIcon className="w-4 h-4" />
            File imports use DimVisitor blobs or local folder — configure template in{' '}
            <Link to="/monitor-safe/daily-data/settings" className="underline">Settings</Link>.
          </p>
        )}
      </div>

      {data?.trading_days?.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent trading days</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Day</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Imported</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Updated</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Run at</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.trading_days.map((d) => (
                  <tr key={d.trading_day}>
                    <td className="px-4 py-2">{formatTradingDay(d.trading_day)}</td>
                    <td className="px-4 py-2">{statusBadge(d.status)}</td>
                    <td className="px-4 py-2">{d.imported ?? '—'}</td>
                    <td className="px-4 py-2">{d.updated ?? '—'}</td>
                    <td className="px-4 py-2">{formatDateTime(d.run_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div ref={resultRef}>
        <ImportResultBanner message={message} onDismiss={() => setMessage(null)} />
      </div>

      {confirmOpts && (
        <ImportConfirmModal
          open={!!confirmOpts}
          importOpts={confirmOpts}
          feedId="visitor"
          feedTitle="Visitors"
          onClose={() => { if (!running) setConfirmOpts(null) }}
          onConfirm={() => runImport({ tradingDay: confirmOpts.tradingDay })}
          confirming={running}
        />
      )}
    </div>
  )
}
